import { Environment } from 'fym-common/src/config/types';
import type {
  Music24EventName,
  Music24EventProps,
  Music24Screen,
} from '../../../../../music24-api/src/productEvents/music24EventCatalog';
import { config } from '../../../config/config';
import { EventPropValue, EventsClient, EventsEnvironment, EventsStorage, TrackOptions } from './hernasEventsClient';
import { browserContext, sessionProps } from './browserContext';
import { randomUuid } from './randomUuid';

/**
 * The product events of the web app (the Hernas Events API, catalog
 * `music24-api/src/productEvents/music24EventCatalog.ts`).
 *
 * Nothing exists before the visitor accepts analytics: `startProductEvents` runs from
 * `SMLGenericAnalytics.enableTracking` (consent bar), and only then is the install id made.
 * `stopProductEvents` (Reject after an Accept) deletes the queue and the install id.
 * With no public key a local build prints the events and every other build sends nothing.
 */
let client: EventsClient | null = null;
let currentScreen: Music24Screen | undefined;

/** The queue and the install id live in localStorage; the session lives per tab. */
const storage = (): EventsStorage | null => {
  try {
    const local = window.localStorage;
    const perTab = window.sessionStorage;
    return {
      get: (key) => (key.endsWith(':session') ? perTab : local).getItem(key),
      set: (key, value) => (key.endsWith(':session') ? perTab : local).setItem(key, value),
      remove: (key) => (key.endsWith(':session') ? perTab : local).removeItem(key),
    };
  } catch {
    return null;
  }
};

const browserEnvironment = (): EventsEnvironment => ({
  now: () => Date.now(),
  fetch: (url, init) => window.fetch(url, init),
  sendBeacon: typeof navigator.sendBeacon === 'function' ? (url, body) => navigator.sendBeacon(url, body) : undefined,
  setInterval: (handler, ms) => window.setInterval(handler, ms),
  clearInterval: (handle) => window.clearInterval(handle as number),
  uuid: () => randomUuid(),
});

const onPageHidden = (): void => {
  if (document.visibilityState === 'hidden') {
    client?.flushOnExit();
  }
};
const onPageHide = (): void => client?.flushOnExit();

export function startProductEvents(): void {
  if (client !== null) {
    return;
  }
  const key = config.hernasEventsPublicKey;
  const debug = config.environment === Environment.local;
  if (key === '' && !debug) {
    return;
  }
  client = new EventsClient({
    baseUrl: config.hernasEventsURL,
    key,
    context: browserContext(),
    storage: storage(),
    environment: browserEnvironment(),
    debug: key === '' || debug,
    sessionEvent: { name: 'session_started', props: (firstSession) => sessionProps(firstSession) },
  });
  document.addEventListener('visibilitychange', onPageHidden);
  window.addEventListener('pagehide', onPageHide);
}

export function stopProductEvents(): void {
  document.removeEventListener('visibilitychange', onPageHidden);
  window.removeEventListener('pagehide', onPageHide);
  if (client !== null) {
    client.reset();
    client = null;
    return;
  }
  // No client on this page view, but an earlier one may have stored a queue and an install id.
  const store = storage();
  for (const key of ['queue', 'installId', 'session', 'sending']) {
    try {
      store?.remove(`hxEvents:music24:${key}`);
    } catch {
      // Blocked storage.
    }
  }
}

export function setProductEventsUser(userId: number | undefined, premium?: boolean): void {
  client?.setUser(userId === undefined ? undefined : `${userId}`, premium);
}

export function setProductEventsScreen(screen: Music24Screen): void {
  currentScreen = screen;
}

/** Sends one catalog event. It does nothing before consent. */
export function trackProductEvent<N extends Music24EventName>(
  name: N,
  props?: Music24EventProps<N>,
  options: TrackOptions = {}
): void {
  if (client === null) {
    return;
  }
  const flat: Record<string, EventPropValue> = {};
  for (const [key, value] of Object.entries(props ?? {}) as [string, unknown][]) {
    if (
      typeof value === 'string' ||
      typeof value === 'boolean' ||
      (typeof value === 'number' && Number.isFinite(value))
    ) {
      flat[key] = typeof value === 'string' ? value.slice(0, 200) : value;
    }
  }
  client.track(name, flat, { screen: currentScreen, ...options });
}
